import { Calendar, Clock, Printer, Undo2, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { useHasRole } from '@/shared/hooks/useRole';
import { Breadcrumb, Button, ErrorState, StatusPill } from '@/shared/ui';
import { formatDate } from '@/shared/utils/date';
import { cn } from '@/shared/utils/cn';

import { ContinuePendingPaymentButton } from '../components/ContinuePendingPaymentButton';
import { OrderBillDialog } from '../components/OrderBillDialog';
import { OrderCancelReturnDialog } from '../components/OrderCancelReturnDialog';
import { OrderFulfilmentStatusActions } from '../components/OrderFulfilmentStatusActions';
import { OrderDetailMainColumn } from '../components/OrderDetailMainColumn';
import { OrderDetailSidebarColumn } from '../components/OrderDetailSidebarColumn';
import { PendingPaymentContinuationNote } from '../components/PendingPaymentContinuationNote';
import { useOrderDetailQuery } from '../api/ordersApi';
import { useContinuePendingPaymentAction } from '../hooks/useContinuePendingPaymentAction';
import { canAdminCancel, canAdminReturn } from '../lib/orderCancelReturn';
import { canContinuePendingPayment } from '../lib/pendingPaymentContinuation';
import { orderStatusIcon, orderStatusTone } from '../lib/orderStatusPresentation';
import type { OrderCancelReturnAction } from '../types';

export function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const { t } = useTranslation('orders');
  const { t: tNav } = useTranslation('common');
  const [dialogAction, setDialogAction] = useState<OrderCancelReturnAction | null>(null);
  const [billOpen, setBillOpen] = useState(false);

  const canContinue = useHasRole('admin');
  const { continueOrder, continuingOrderId } = useContinuePendingPaymentAction();
  const { data: detail, isLoading, isError } = useOrderDetailQuery(orderId);

  if (isLoading) {
    return (
      <div data-page="orders/detail" className="mx-auto w-full px-4 py-8">
        <div className="flex justify-center">
          <div className="h-8 w-8">
            <svg className="h-8 w-8 animate-spin text-primary" viewBox="0 0 24 24" />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !detail) {
    return (
      <div data-page="orders/detail-not-found" className="mx-auto w-full px-4 py-8">
        <Breadcrumb
          className="mb-6"
          items={[
            { label: tNav('nav.orders' as Parameters<typeof tNav>[0]), to: '/orders' },
            { label: orderId ?? '—', current: true },
          ]}
        />
        <ErrorState title={t('detail.notFound.title')} message={t('detail.notFound.message')} />
        <div className="mt-6 flex justify-center">
          <Button type="button" variant="secondary" asChild>
            <Link to="/orders">{t('detail.notFound.cta')}</Link>
          </Button>
        </div>
      </div>
    );
  }

  const status = detail.status;
  const StatusIcon = orderStatusIcon[status];
  const placedDate = formatDate(detail.placedAtIso, 'date');
  const placedTime = formatDate(detail.placedAtIso, 'time');
  const showCancel = canAdminCancel(status);
  const showReturn = canAdminReturn(detail);
  const showContinue = canContinue && canContinuePendingPayment(status);
  const continuing = continuingOrderId === detail.id;

  return (
    <div data-page="orders/detail" className="mx-auto flex w-full flex-col gap-4">
      <div className="flex flex-col gap-4">
        <Breadcrumb
          items={[
            { label: tNav('nav.orders' as Parameters<typeof tNav>[0]), to: '/orders' },
            { label: detail.orderNumber || detail.id, current: true },
          ]}
        />

        <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-balance text-2xl font-bold tracking-tight text-fg sm:text-3xl">
                {t('detail.title', { orderNumber: detail.orderNumber || detail.id })}
              </h1>
              <StatusPill tone={orderStatusTone[status]} icon={StatusIcon}>
                {t(`status.${status}`)}
              </StatusPill>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm text-fg-muted">
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-4 w-4 shrink-0" aria-hidden />
                <span>{placedDate}</span>
              </span>
              <span className="h-1 w-1 shrink-0 rounded-full bg-border-strong" aria-hidden />
              <span className="inline-flex items-center gap-1">
                <Clock className="h-4 w-4 shrink-0" aria-hidden />
                <span>{placedTime}</span>
              </span>
            </div>
            <PendingPaymentContinuationNote
              resolution={detail.pendingPaymentResolution}
              continuedAtIso={detail.pendingPaymentContinuedAt}
              continuedBy={detail.pendingPaymentContinuedBy}
              t={t}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 lg:shrink-0">
            {showContinue ? (
              <ContinuePendingPaymentButton
                size="md"
                pending={continuing}
                t={t}
                onClick={() => {
                  void continueOrder({ id: detail.id, orderNumber: detail.orderNumber });
                }}
              />
            ) : null}
            <OrderFulfilmentStatusActions
              orderId={detail.id}
              orderNumber={detail.orderNumber || detail.id}
              status={status}
            />
            <Button
              type="button"
              variant="secondary"
              className="gap-2 shadow-sm"
              onClick={() => setBillOpen(true)}
            >
              <Printer className="h-4 w-4" aria-hidden />
              {t('detail.actions.printInvoice')}
            </Button>
            {showCancel ? (
              <Button
                type="button"
                variant="danger"
                className="gap-2 shadow-sm"
                onClick={() => setDialogAction('CANCEL')}
              >
                <X className="h-4 w-4" aria-hidden />
                {t('detail.actions.cancelOrder')}
              </Button>
            ) : null}
            {showReturn ? (
              <Button
                type="button"
                variant="secondary"
                className="gap-2 shadow-sm"
                onClick={() => setDialogAction('RETURN')}
              >
                <Undo2 className="h-4 w-4" aria-hidden />
                {t('detail.actions.returnOrder')}
              </Button>
            ) : null}
          </div>
        </header>

        <div className={cn('grid gap-8 lg:grid-cols-12 lg:gap-10')}>
          <OrderDetailMainColumn detail={detail} t={t} />
          <OrderDetailSidebarColumn detail={detail} t={t} />
        </div>
      </div>

      <OrderBillDialog detail={detail} open={billOpen} onOpenChange={setBillOpen} />
      <OrderCancelReturnDialog
        open={Boolean(dialogAction)}
        orderId={detail.id}
        action={dialogAction}
        onOpenChange={(open) => {
          if (!open) setDialogAction(null);
        }}
      />
    </div>
  );
}
